{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "title": "MeesterJay Visual Aid Schemas",
  "description": "Canonical runtime schemas keyed by VisualAidType for validation. The 'usage' field on each definition declares whether the visual is intended as 'stimulus', 'support', or both — used by the governance pipeline to flag misuse (e.g. a support_only visual placed in stimulusAids).",
  "version": "5.7.0",
  "definitions": {
    "baseVisualAid": { "type": "object", "required": ["type", "data"], "properties": { "type": { "type": "string", "enum": ["stairs","fraction","geo","rule","numberline","ratio","money","column","clock","digitalClock","decimalgrid","verbTable","kofschipMnemonic","syllableBlocks","circle","coordinateGrid","box3d","compositeShape","geometricShape","readingPassage","barChart","lineChart","calendar","thermometer","ruler","measuringCup","weighScale","equalSharing","percentageBar","fractionStrip","splitDiagram","sentenceDiagram","mapScale","protractor","symmetryGrid","numberBoard","speedDiagram","priceCompare","timeline","worldClockPanel","receipt","budgetBar","wordClassChip","signalWordCard","synonymAntonymPair","textStructureSchema","referenceArrow"] }, "data": { "type": "object" }, "showWhen": { "type": "string", "enum": ["always", "wrong", "correct"], "default": "always" } } },
    "stairs": { "required": ["type", "from", "to"], "properties": { "type": { "type": "string", "enum": ["length", "weight", "volume"] }, "from": { "type": "string" }, "to": { "type": "string" }, "value": { "type": "number" }, "highlight": { "type": "boolean" }, "showAnimation": { "type": "boolean" } } },
    "fraction": { "required": ["numerator", "denominator"], "properties": { "numerator": { "type": "number", "minimum": 0 }, "denominator": { "type": "number", "minimum": 1 }, "visualType": { "type": "string", "enum": ["pizza", "cake", "chocolate", "marbles", "bar"] }, "label": { "type": "string" }, "showEaten": { "type": "boolean" } } },
    "geo": { "required": ["width", "height"], "properties": { "width": { "type": "number", "minimum": 1 }, "height": { "type": "number", "minimum": 1 }, "unit": { "type": "string" }, "label": { "type": "string" } } },
    "numberline": { "required": ["start", "end"], "properties": { "start": { "type": "number" }, "end": { "type": "number" }, "step": { "type": "number" }, "highlight": { "type": "array", "items": { "type": "number" } }, "marked": { "type": "array", "items": { "type": "number" } }, "arrows": { "type": "array" }, "label": { "type": "string" }, "showArrow": { "type": "boolean" } } },
    "ratio": { "required": ["headers", "rows"], "properties": { "headers": { "type": "array", "items": { "type": "string" } }, "rows": { "type": "array", "items": { "type": "array" } } } },
    "rule": { "required": ["title", "content"], "properties": { "title": { "type": "string" }, "content": { "type": "string" }, "icon": { "type": "string" }, "color": { "type": "string" } } },
    "money": { "required": ["amount"], "properties": { "amount": { "type": "number" }, "breakdown": { "type": "object" }, "showCoins": { "type": "boolean" }, "showBills": { "type": "boolean" }, "interactive": { "type": "boolean" }, "label": { "type": "string" } } },
    "column": { "required": ["operation", "operand1", "operand2"], "properties": { "operation": { "type": "string", "enum": ["add", "subtract", "multiply", "divide"] }, "operand1": { "type": "number" }, "operand2": { "type": "number" }, "showSteps": { "type": "boolean" }, "currentStep": { "type": "number" }, "animated": { "type": "boolean" } } },
    "clock": { "required": [], "properties": { "hour": { "type": "number", "minimum": 0, "maximum": 23 }, "minute": { "type": "number", "minimum": 0, "maximum": 59 }, "hours": { "type": "number", "minimum": 0, "maximum": 23 }, "minutes": { "type": "number", "minimum": 0, "maximum": 59 }, "label": { "type": "string" } }, "anyOfRequired": [["hour", "minute"], ["hours", "minutes"]], "description": "Accepts either hour/minute (singular) or hours/minutes (plural) field names. At least one pair must be present." },
    "digitalClock": { "required": [], "properties": { "hour": { "type": "number", "minimum": 0, "maximum": 23 }, "minute": { "type": "number", "minimum": 0, "maximum": 59 }, "hours": { "type": "number", "minimum": 0, "maximum": 23 }, "minutes": { "type": "number", "minimum": 0, "maximum": 59 }, "seconds": { "type": "number", "minimum": 0, "maximum": 59 }, "showSeconds": { "type": "boolean" }, "format": { "type": "string", "enum": ["12h", "24h"] }, "label": { "type": "string" } }, "anyOfRequired": [["hour", "minute"], ["hours", "minutes"]], "description": "Accepts either hour/minute (singular) or hours/minutes (plural) field names. At least one pair must be present." },
    "decimalgrid": { "required": [], "properties": { "cols": { "type": "number" }, "rows": { "type": "number" }, "highlighted": { "type": "number" }, "label": { "type": "string" } } },
    "verbTable": {
      "description": "Supports two formats: (A) TT-style with persons/forms arrays, (B) VT-style with forms object containing named tenses",
      "required": ["verb"],
      "properties": {
        "verb": { "type": "string" },
        "tense": { "type": "string", "enum": ["present", "past", "perfect"] },
        "persons": { "type": "array", "items": { "type": "string" } },
        "forms": {
          "oneOf": [
            { "type": "array", "items": { "type": "string" } },
            { "type": "object", "properties": {
              "infinitive": { "type": "string" },
              "presentSingular": { "type": "string" },
              "pastSingular": { "type": "string" },
              "pastPlural": { "type": "string" },
              "pastParticiple": { "type": "string" }
            } }
          ]
        },
        "highlight": { "type": "string" },
        "label": { "type": "string" }
      }
    },
    "kofschipMnemonic": { "required": ["verb", "stem", "lastLetter", "useTE"], "properties": { "verb": { "type": "string" }, "stem": { "type": "string" }, "lastLetter": { "type": "string" }, "useTE": { "type": "boolean" }, "label": { "type": "string" } } },
    "syllableBlocks": { "required": ["word", "syllables", "types"], "properties": { "word": { "type": "string" }, "syllables": { "type": "array", "items": { "type": "string" } }, "types": { "type": "array", "items": { "type": "string", "enum": ["open", "closed"] } }, "highlight": { "type": "number" }, "label": { "type": "string" } } },
    "circle": { "required": [], "properties": { "radius": { "type": "number", "minimum": 0 }, "diameter": { "type": "number", "minimum": 0 }, "showRadius": { "type": "boolean" }, "showDiameter": { "type": "boolean" }, "showFormula": { "type": "boolean" }, "label": { "type": "string" }, "unit": { "type": "string" } } },
    "coordinateGrid": { "required": [], "properties": { "xMin": { "type": "number" }, "xMax": { "type": "number" }, "yMin": { "type": "number" }, "yMax": { "type": "number" }, "points": { "type": "array" }, "gridLines": { "type": "boolean" }, "label": { "type": "string" } } },
    "box3d": { "required": [], "properties": { "length": { "type": "number", "minimum": 0 }, "width": { "type": "number", "minimum": 0 }, "height": { "type": "number", "minimum": 0 }, "unit": { "type": "string" }, "showDimensions": { "type": "boolean" }, "label": { "type": "string" } } },
    "compositeShape": { "required": [], "properties": { "shapes": { "type": "array" }, "showDimensions": { "type": "boolean" }, "label": { "type": "string" }, "unit": { "type": "string" } } },
    "geometricShape": { 
      "required": ["shape", "base", "height"], 
      "properties": { 
        "shape": { "type": "string", "enum": ["rectangle", "triangle", "parallelogram", "trapezoid"], "description": "The type of geometric shape to display" }, 
        "base": { "type": "number", "minimum": 0 }, 
        "height": { "type": "number", "minimum": 0 }, 
        "sideLength": { "type": "number", "minimum": 0 }, 
        "unit": { "type": "string" }, 
        "showGrid": { "type": "boolean" }, 
        "showDimensions": { "type": "boolean" }, 
        "hideHeight": { "type": "boolean", "description": "Hide height dimension when it's the answer to the question" },
        "label": { "type": "string" } 
      } 
    },
    "equalSharing": {
      "required": ["total", "parts", "perPart"],
      "properties": {
        "total": { "type": "number", "minimum": 0 },
        "parts": { "type": "number", "minimum": 1 },
        "perPart": { "type": "number", "minimum": 0 },
        "unit": { "type": "string" },
        "label": { "type": "string" }
      }
    },
    "readingPassage": { "required": ["passage"], "properties": { "passage": { "type": "string", "minLength": 1, "description": "The reading passage text" }, "instruction": { "type": "string", "description": "Instruction label, e.g. 'Lees de tekst'" }, "source": { "type": "string", "description": "Source attribution, e.g. 'Fragment uit De Kleine Prins'" }, "label": { "type": "string", "description": "Optional label shown above the passage" } } },
    "barChart": { "required": ["data"], "properties": { "data": { "type": "array", "items": { "type": "object", "required": ["label", "value"], "properties": { "label": { "type": "string" }, "value": { "type": "number" } } } }, "xAxisLabel": { "type": "string" }, "yAxisLabel": { "type": "string" }, "barColor": { "type": "string" }, "hideValues": { "type": "boolean" } } },
    "lineChart": { "required": ["data"], "properties": { "data": { "type": "array", "items": { "type": "object", "required": ["label", "value"], "properties": { "label": { "type": "string" }, "value": { "type": "number" } } } }, "xAxisLabel": { "type": "string" }, "yAxisLabel": { "type": "string" }, "lineColor": { "type": "string" }, "hideValues": { "type": "boolean" } } },
    "calendar": {
      "required": ["year", "month"],
      "properties": {
        "year": { "type": "number" },
        "month": { "type": "number", "minimum": 1, "maximum": 12 },
        "highlightDates": { "type": "array", "items": { "type": "number" } },
        "todayDate": { "type": "number" },
        "targetDate": { "type": "number" },
        "secondMonth": { "type": "number", "minimum": 1, "maximum": 12 },
        "secondMonthYear": { "type": "number" },
        "secondMonthHighlight": { "type": "array", "items": { "type": "number" } },
        "secondMonthTarget": { "type": "number" },
        "label": { "type": "string" }
      }
    },
    "thermometer": {
      "required": ["currentTemp"],
      "properties": {
        "currentTemp": { "type": "number", "description": "Current temperature to display" },
        "minTemp": { "type": "number", "description": "Minimum temperature on scale (default -20)" },
        "maxTemp": { "type": "number", "description": "Maximum temperature on scale (default 50)" },
        "targetTemp": { "type": "number", "description": "Optional target temperature marker" },
        "unit": { "type": "string", "enum": ["C", "F"], "description": "Temperature unit (default C)" },
        "label": { "type": "string" }
      }
    },
    "ruler": {
      "required": [],
      "properties": {
        "length": { "type": "number", "minimum": 1, "description": "Max cm shown (default 15)" },
        "objectStart": { "type": "number", "minimum": 0, "description": "Where measured object starts (cm)" },
        "objectEnd": { "type": "number", "minimum": 0, "description": "Where measured object ends (cm)" },
        "unit": { "type": "string", "enum": ["cm", "mm"], "description": "Unit (default cm)" },
        "hideValue": { "type": "boolean", "description": "Hide measurement in stimulus mode" },
        "label": { "type": "string" }
      }
    },
    "measuringCup": {
      "required": ["maxVolume", "currentVolume"],
      "properties": {
        "maxVolume": { "type": "number", "minimum": 1, "description": "Maximum volume capacity" },
        "currentVolume": { "type": "number", "minimum": 0, "description": "Current liquid level" },
        "targetVolume": { "type": "number", "minimum": 0, "description": "Optional target volume marker" },
        "unit": { "type": "string", "enum": ["ml", "l", "cl"], "description": "Volume unit (default ml)" },
        "hideValue": { "type": "boolean", "description": "Hide volume value in stimulus mode" },
        "label": { "type": "string" }
      }
    },
    "weighScale": {
      "required": ["leftWeight", "rightWeight"],
      "properties": {
        "leftWeight": { "type": "number", "minimum": 0, "description": "Weight on left plate" },
        "rightWeight": { "type": "number", "minimum": 0, "description": "Weight on right plate" },
        "leftLabel": { "type": "string", "description": "Label for left plate (e.g. '? kg')" },
        "rightLabel": { "type": "string", "description": "Label for right plate (e.g. '3 kg')" },
        "unit": { "type": "string", "enum": ["g", "kg"], "description": "Weight unit (default kg)" },
        "showQuestion": { "type": "boolean", "description": "Show ? on one side" },
        "label": { "type": "string" }
      }
    },
    "percentageBar": {
      "required": ["percentage"],
      "properties": {
        "percentage": { "type": "number", "minimum": 0, "maximum": 100, "description": "The percentage to display" },
        "comparePercentage": { "type": "number", "minimum": 0, "maximum": 100, "description": "Optional second percentage for comparison" },
        "percentageLabel": { "type": "string", "description": "Label for the primary bar" },
        "compareLabel": { "type": "string", "description": "Label for the comparison bar" },
        "showValue": { "type": "boolean", "description": "Show percentage text on the bar (default true)" },
        "label": { "type": "string" }
      }
    },
    "fractionStrip": {
      "required": ["fractions"],
      "properties": {
        "fractions": { "type": "array", "minItems": 1, "items": { "type": "object", "required": ["numerator", "denominator"], "properties": { "numerator": { "type": "number", "minimum": 0 }, "denominator": { "type": "number", "minimum": 1 }, "label": { "type": "string" } } }, "description": "Array of fractions to display as strips" },
        "highlightIndex": { "type": "number", "minimum": 0, "description": "Index of fraction to highlight" },
        "label": { "type": "string" }
      }
    },
    "splitDiagram": {
      "required": ["original", "parts"],
      "properties": {
        "original": { "type": "number", "description": "The number being split" },
        "parts": { "type": "array", "minItems": 1, "items": { "type": "object", "required": ["value"], "properties": { "value": { "type": "number" }, "label": { "type": "string" } } }, "description": "Parts the number is split into" },
        "operation": { "type": "string", "enum": ["add", "subtract", "multiply"], "description": "Operation context (default: add)" },
        "operand": { "type": "number", "description": "The other operand in the calculation" },
        "result": { "type": "number", "description": "Final result of the calculation" },
        "label": { "type": "string" }
      }
    },
    "sentenceDiagram": {
      "required": ["sentence", "parts"],
      "properties": {
        "sentence": { "type": "string", "description": "The full sentence to diagram" },
        "parts": { "type": "array", "minItems": 1, "items": { "type": "object", "required": ["text", "role"], "properties": { "text": { "type": "string" }, "role": { "type": "string" }, "abbr": { "type": "string" } } }, "description": "Parsed sentence parts with grammatical roles" },
        "label": { "type": "string" }
      }
    },
    "mapScale": {
      "required": ["scaleRatio"],
      "properties": {
        "scaleRatio": { "type": "number", "minimum": 1, "description": "The scale ratio (e.g. 10000 for 1:10.000)" },
        "mapLength": { "type": "number", "minimum": 0, "description": "Length on the map in cm" },
        "realLength": { "type": "number", "minimum": 0, "description": "Real-world length" },
        "unit": { "type": "string", "enum": ["cm", "m", "km"], "description": "Unit for real-world length (default km)" },
        "label": { "type": "string" }
      }
    },
    "protractor": {
      "required": ["angle"],
      "properties": {
        "angle": { "type": "number", "minimum": 0, "maximum": 360, "description": "The angle to display in degrees" },
        "compareAngle": { "type": "number", "minimum": 0, "maximum": 360, "description": "Optional second angle for comparison" },
        "showValue": { "type": "boolean", "description": "Show the angle value (default true)" },
        "angleType": { "type": "string", "enum": ["scherp", "recht", "stomp", "gestrekt", "overig"], "description": "Type of angle" },
        "label": { "type": "string" }
      }
    },
    "symmetryGrid": {
      "required": [],
      "properties": {
        "rows": { "type": "number", "minimum": 2, "description": "Number of grid rows (default 6)" },
        "cols": { "type": "number", "minimum": 2, "description": "Number of grid columns (default 6)" },
        "shadedCells": { "type": "array", "items": { "type": "number" }, "description": "Indices of shaded cells (row-major)" },
        "axis": { "type": "string", "enum": ["vertical", "horizontal", "diagonal"], "description": "Axis of symmetry" },
        "showMirror": { "type": "boolean", "description": "Whether to show the mirror reflection" },
        "label": { "type": "string" }
      }
    },
    "numberBoard": {
      "required": [],
      "properties": {
        "start": { "type": "number", "description": "Starting number (default 1)" },
        "end": { "type": "number", "description": "Ending number (default 100)" },
        "highlight": { "type": "array", "items": { "type": "number" }, "description": "Numbers to highlight" },
        "marked": { "type": "array", "items": { "type": "number" }, "description": "Numbers to mark with secondary color" },
        "columns": { "type": "number", "minimum": 1, "description": "Number of columns (default 10)" },
        "showPattern": { "type": "boolean", "description": "Show pattern explanation" },
        "label": { "type": "string" }
      }
    },
    "speedDiagram": {
      "required": [],
      "properties": {
        "speed": { "type": "number", "minimum": 0, "description": "Speed value" },
        "distance": { "type": "number", "minimum": 0, "description": "Distance value" },
        "time": { "type": "number", "minimum": 0, "description": "Time value" },
        "speedUnit": { "type": "string", "description": "Unit for speed (default km/u)" },
        "distanceUnit": { "type": "string", "description": "Unit for distance (default km)" },
        "timeUnit": { "type": "string", "description": "Unit for time (default uur)" },
        "unknown": { "type": "string", "enum": ["speed", "distance", "time"], "description": "Which value is unknown" },
        "label": { "type": "string" }
      }
    },
    "priceCompare": {
      "required": ["items"],
      "properties": {
        "items": { "type": "array", "minItems": 2, "items": { "type": "object", "required": ["name", "quantity", "unit", "totalPrice"], "properties": { "name": { "type": "string" }, "quantity": { "type": "number", "minimum": 0 }, "unit": { "type": "string" }, "totalPrice": { "type": "number", "minimum": 0 } } }, "description": "Products/offers to compare" },
        "compareUnit": { "type": "string", "description": "Unit label for comparison (default: per stuk)" },
        "label": { "type": "string" }
      }
    },
    "timeline": {
      "required": ["events"],
      "properties": {
        "events": { "type": "array", "items": { "type": "object", "required": ["position", "label"], "properties": { "position": { "type": "number" }, "label": { "type": "string" }, "description": { "type": "string" }, "isHighlighted": { "type": "boolean" } } } },
        "start": { "type": "number" },
        "end": { "type": "number" },
        "label": { "type": "string" }
      }
    },
    "worldClockPanel": {
      "required": ["clocks"],
      "properties": {
        "clocks": {
          "type": "array",
          "minItems": 1,
          "items": {
            "type": "object",
            "required": ["city", "time", "utcOffset"],
            "properties": {
              "city": { "type": "string" },
              "time": { "type": "string" },
              "utcOffset": { "type": "number" },
              "isReference": { "type": "boolean" }
            }
          }
        },
        "label": { "type": "string" }
      }
    },
    "receipt": {
      "required": ["items"],
      "properties": {
        "items": { "type": "array", "minItems": 1, "items": { "type": "object", "required": ["name", "price"], "properties": { "name": { "type": "string" }, "price": { "type": "number" }, "quantity": { "type": "number", "minimum": 1 } } }, "description": "Products on the receipt with name and price" },
        "storeName": { "type": "string", "description": "Name of the store shown on the receipt header" },
        "showTotal": { "type": "boolean", "description": "Whether to show the total sum (default true)" },
        "totalBudget": { "type": "number", "minimum": 0, "description": "Optional budget amount to compare against total" },
        "label": { "type": "string" }
      }
    },
    "budgetBar": {
      "required": ["totalBudget", "segments"],
      "properties": {
        "totalBudget": { "type": "number", "minimum": 0, "description": "Total budget amount in euros" },
        "segments": { "type": "array", "minItems": 1, "items": { "type": "object", "required": ["label", "amount"], "properties": { "label": { "type": "string" }, "amount": { "type": "number", "minimum": 0 }, "color": { "type": "string" } } }, "description": "Budget segments with label and amount" },
        "showRemaining": { "type": "boolean", "description": "Whether to show remaining budget (default true)" },
        "label": { "type": "string" }
      }
    },
    "wordClassChip": {
      "usage": ["stimulus", "support"],
      "required": [],
      "properties": {
        "highlightClass": { "type": "string", "enum": ["lidwoord", "zelfstandig naamwoord", "bijvoeglijk naamwoord", "werkwoord", "bijwoord", "voornaamwoord", "voorzetsel", "voegwoord", "telwoord"], "description": "Word class to visually highlight in the chip strip" },
        "showLegend": { "type": "boolean", "description": "Show full word-class name under the chips when one is highlighted (default true)" },
        "label": { "type": "string" }
      },
      "description": "Strip of 9 Dutch word-class chips (LW, ZN, BNW, WW, BW, VNW, VZ, VW, TW). Pairs with text_highlight 'tap_word_class' mode and with all SOORT-* and ONT-* families (TAA module). Audit ref: TAA_VRAAGTYPEN_VISUALS_AUDIT_2026 §V1."
    },
    "signalWordCard": {
      "usage": ["stimulus", "support"],
      "required": ["word", "function"],
      "properties": {
        "word": { "type": "string", "minLength": 1, "description": "The Dutch signal word to display, e.g. 'daardoor', 'hoewel', 'eerst'" },
        "function": { "type": "string", "enum": ["oorzaak-gevolg", "tijd", "tegenstelling", "toevoeging", "overig"], "description": "The discourse function of the signal word — drives the icon and color coding" },
        "example": { "type": "string", "description": "Optional short example sentence using the signal word" },
        "label": { "type": "string", "description": "Override for the function label shown on the card" }
      },
      "description": "Compact card showing a Dutch signal word with its discourse function (color-coded + icon). Pairs with the multiple_choice 'icon_chips' mode for SIGNAALWOORD/LEZEN-VERB/LEZEN-STRUCTUUR. Audit ref: TAA_VRAAGTYPEN_VISUALS_AUDIT_2026 §V2."
    },
    "synonymAntonymPair": {
      "usage": ["stimulus", "support"],
      "required": ["leftWord", "rightWord", "relation"],
      "properties": {
        "leftWord": { "type": "string", "minLength": 1, "description": "Left-hand word in the pair" },
        "rightWord": { "type": "string", "minLength": 1, "description": "Right-hand word in the pair" },
        "relation": { "type": "string", "enum": ["synoniem", "antoniem"], "description": "Relation between the two words — drives the = vs ≠ symbol and color coding" },
        "distractorWord": { "type": "string", "description": "Optional third word displayed as a struck-through distractor (support-context only)" },
        "label": { "type": "string" }
      },
      "description": "Two words side-by-side connected by '=' (synoniem) or '≠' (antoniem), color-coded. Pairs with WOORD-SYNONIEM, WOORD-ANTONIEM, WOORD-CONTEXT families. Audit ref: TAA_VRAAGTYPEN_VISUALS_AUDIT_2026 §V5."
    },
    "textStructureSchema": {
      "usage": ["stimulus", "support"],
      "required": ["structure"],
      "properties": {
        "structure": { "type": "string", "enum": ["probleem-oplossing", "oorzaak-gevolg", "vergelijking", "chronologisch"], "description": "Which of the four canonical Dutch text-structure templates to render" },
        "labels": { "type": "array", "items": { "type": "string" }, "description": "Optional override for the default Dutch box labels. Length must match the template (2 for flow/compare, 3 for timeline)." },
        "highlightIndex": { "type": "number", "minimum": 0, "description": "Optional 0-based index of the box to highlight (e.g. to point at the answer)" },
        "label": { "type": "string", "description": "Override for the figcaption shown above the schema" }
      },
      "description": "Compact box-and-arrow diagram of the four canonical text structures (probleem-oplossing, oorzaak-gevolg, vergelijking, chronologisch). Pairs with TAA-LEZEN-STRUCTUUR, TAA-LEZEN-KERN, TAA-TEKSTSOORT. Audit ref: TAA_VRAAGTYPEN_VISUALS_AUDIT_2026 §V4 / S4."
    },
    "referenceArrow": {
      "usage": ["stimulus", "support"],
      "required": ["sentence", "pronounIndex", "referentIndex"],
      "properties": {
        "sentence": { "type": "string", "minLength": 1, "description": "Full passage text. Words are split on whitespace and indexed left-to-right starting at 0." },
        "pronounIndex": { "type": "number", "minimum": 0, "description": "0-based word index of the verwijswoord (e.g. 'daar', 'ze', 'die')" },
        "referentIndex": { "type": "number", "minimum": 0, "description": "0-based word index of the woord-where-the-pronoun-points-to" },
        "label": { "type": "string" }
      },
      "description": "Visualizes a verwijzing in a Dutch passage as a curved arrow from the pronoun (indigo) to its referent (emerald). Pairs with the text_highlight 'tap_referent' mode (Q1) for TAA-LEZEN-VERW. Audit ref: TAA_VRAAGTYPEN_VISUALS_AUDIT_2026 §V3 / S5."
    }
  }
}